import { observable } from 'mobx';
import BaseModalStore from '@src/components/modal-container/base-modal-store';
import { noop } from '@src/utils/common-util';

import TinyInputStore from '@src/components/tiny-input/store';
import AntdMessage from '@src/components/antd/message';
import { autobind } from 'core-decorators';
import lockAsync from '@src/utils/lock-async';
import captchaHandlerDecorate from '@src/utils/captcha-deco-util';
import { useRequest } from 'ahooks';
import { isUndefined } from '@src/utils/type-utils';
import { useEffect } from 'react';
import { message } from 'antd';
import getUtmSource from '@src/utils/get-utm-source';
import { getLocales } from '@src/global-locales/get-page-locales-data';
import useMount from '@src/utils/hooks/use-mount';

class AppointmentModalStore extends BaseModalStore {
  @observable closeCallback = noop;
  // 协议勾选状态
  @observable protocolCheckState = false;
  // 当前主机tab标准
  @observable channelId = 0;
  /**
   * 手机号码输入框
   * @type {TinyInputStore}
   */
  phoneInputStore = new TinyInputStore({
    onChange: value => {
      let val = value?.target?.value;
      if (!isUndefined(val)) {
        val = val.replace(/[^A-Za-z0-9\u4e00-\u9fa5 ]/g, '');
        // val = val.replace(/\D/g, '');
        val = val.slice(0, 11);
      }
      this.phoneInputStore.value = val;
    }
  });
  /**
   * 验证码输入框
   * @type {TinyInputStore}
   */
  smsInputStore = new TinyInputStore();
  @observable submitLock = false;
  /**
   * 预约成功
   */
  @observable appointmentSuccessCallBack = noop;
  @observable appointmentSuccessState = false;
  // 预约成功后自动关闭的倒计时
  @observable appointmentSuccessCloseTime = 5;
  // 手机栏错误提示
  @observable phoneInputError = '';
  // 验证码栏错误提示
  @observable smsInputError = '';

  utmSource = getUtmSource();

  constructor(options = {}) {
    super();
    Object.assign(this, options);
  }

  /**
   * 点击修改渠道
   * @param channelId
   */
  onChangeChannel = channelId => {
    this.channelId = channelId;
  };
  /**
   * 点击执行预约
   */
  @lockAsync()
  onExecuteAppointment = async () => {
    if (this.checkValidate() && !this.submitLock) {
      this.submitLock = true;
      await this.executeAppointment.run();
    }
  };

  /**
   * 点击同意协议状态
   */
  clickProtocol = () => {
    this.protocolCheckState = !this.protocolCheckState;
  };

  /**
   * 点击关闭弹窗
   * 重写关闭事件, 补充了弹窗特定的关闭回调
   */
  onClickClose = () => {
    this.isOpen = false;
    this.isActive = false;
    this.protocolCheckState = false;
    this.appointmentSuccessState = false;
    this.phoneInputStore.onChange('');
    this.smsInputStore.onChange('');
    this.closeCallback();
    sessionStorage.removeItem('smsFirstTimes');
    this.clearStyle();
  };
  /**
   * 校验输入
   * @returns {boolean}
   */
  checkValidate = () => {
    let validate = false;
    if (!this.phoneInputStore.value) {
      this.phoneInputError = getLocales('reserve.logic.need_phone');
    } else if (!this.smsInputStore.value) {
      this.smsInputError = getLocales('reserve.logic.need_vcode');
    } else if (!this.protocolCheckState) {
      message.error(getLocales('reserve.logic.need_agreement'));
    } else {
      validate = true;
    }
    return validate;
  };

  /**
   * 发送验证码
   * @param authParams
   * @returns {Promise<boolean>}
   */
  @autobind
  @lockAsync()
  @captchaHandlerDecorate('zt')
  async onSendVCRequest({ ...authParams }) {
    try {
      await this.onGetPhoneCode.run({ phone: this.phoneInputStore.value, ...authParams });
      return true;
    } catch (err) {
      // message.error(err.message);
      return false;
    }
  }

  // 预约成功处理
  onAppointmentSuccess = () => {
    this.appointmentSuccessState = true;
    this.appointmentSuccessCallBack();
    clearInterval(this.appointmentSuccessTimer);
    this.appointmentSuccessTimer = setInterval(() => {
      if (this.appointmentSuccessCloseTime <= 0) {
        clearInterval(this.appointmentSuccessTimer);
        this.onClickClose();
        return false;
      }
      this.appointmentSuccessCloseTime = this.appointmentSuccessCloseTime - 1;
    }, 1000);
  };

  /**
   * 初始化数据
   */
  reInitState = () => {
    this.appointmentSuccessState = false;
    this.channelId = '';
    this.smsInputStore.onChange('');
    this.phoneInputStore.onChange('');
  };
  // 获取手机验证码
  onGetPhoneCodeAsync = params => {
    return this.$ztService.getPhoneCode(params);
  };
  // 预约请求发送
  appointmentAsync = () => {
    return this.$ztService.subscribe(this.smsInputStore.value, 'default', this.phoneInputStore.value, this.utmSource);
  };

  use() {
    super.use();
    this.executeAppointment = useRequest(async () => await this.appointmentAsync(), {
      manual: true,
      onSuccess: data => {
        this.onAppointmentSuccess();
        this.submitLock = false;
        // $$tracker.clickReserveSubmit('success', this.phoneInputStore.value, this.currentChannelId, '');
        // semTracker.send({
        //   eventId: 'btForm'
        // });
      },
      onError: e => {
        e.serviceResponse?.message && AntdMessage.error(e.serviceResponse?.message);
        this.submitLock = false;
        // $$tracker.clickReserveSubmit('fail', this.phoneInputStore.value, this.currentChannelId, e.serviceResponse?.message);
      }
    });
    this.onGetPhoneCode = useRequest(async params => await this.onGetPhoneCodeAsync(params), {
      manual: true,
      onSuccess: data => {},
      onError: e => {
        e.serviceResponse?.message && AntdMessage.error(e.serviceResponse?.message);
      }
    });

    useMount(() => {
      // 二次删除重复发送状态
      sessionStorage.removeItem('smsFirstTimes');
    });

    useEffect(
      () => {
        if (!!this.phoneInputStore.value) {
          this.phoneInputError = '';
          this.smsInputError = '';
        }
      },
      [this.phoneInputStore.value]
    );
    useEffect(
      () => {
        if (!!this.smsInputStore.value) {
          this.smsInputError = '';
        }
      },
      [this.smsInputStore.value]
    );
  }
}

export default AppointmentModalStore;
